/**
 * StageRouter — Single stageRenderer that owns the current stage view.
 *
 * Listens to navigation events on the bus and swaps the visible view in place.
 * See ADR 0004 for the rationale (no URL router, in-memory navigation only).
 *
 * Current routes:
 *   - home        → AuthorListView
 *   - author/:id  → AuthorDetailView
 *
 * Future routes (concept, argomento, tappa) plug in by adding a case to
 * mountRoute(). Views are destroyed on exit and rebuilt on re-entry; caching
 * can be introduced later if profiling shows the rebuild is a hot path.
 */

import { BaseComponent } from '@/core/BaseComponent';
import { AuthorListView } from './AuthorListView';
import { AuthorDetailView } from './AuthorDetailView';
import { InfluenceGraphView, type InfluenceState } from './InfluenceGraphView';
import { ConceptMapView, type ConceptMapState } from './ConceptMapView';
import { TappaReaderView } from './TappaReaderView';
import { ConceptPathView } from './ConceptPathView';
import { ArgumentDetailView } from './ArgumentDetailView';

type Route =
  | { kind: 'home' }
  | { kind: 'author'; id: string }
  | { kind: 'graph' }
  | { kind: 'concept-map' }
  | { kind: 'tappa'; id: string; conceptId?: string }
  | { kind: 'concept'; id: string; sourceAuthorId?: string }
  | { kind: 'argomento'; id: string };

export class StageRouter extends BaseComponent {
  private currentRoute: Route = { kind: 'home' };
  private currentView: BaseComponent | null = null;
  private viewContainer!: HTMLElement;
  private savedHomeScroll = 0;
  /** Storico rotte+stato → il "← Indietro" torna alla vista PRECEDENTE (mappa,
   *  grafo, altro autore…) e non sempre alla home. Lo stato della vista lasciata
   *  è catturato via captureState() (se esposto) e ripristinato al rientro. */
  private history: Array<{ route: Route; state?: unknown; scroll?: number }> = [];

  protected async onInit(): Promise<void> {
    this.element.classList.add('stage-router');
    this.element.innerHTML = `<div class="stage-router-container"></div>`;
    this.viewContainer = this.element.querySelector<HTMLElement>('.stage-router-container')!;

    // Subscribe to navigation events.
    this.addCleanup(
      this.context.bus.on<{ id: string }>('navigate:author', ({ id }) => {
        this.navigate({ kind: 'author', id });
      }),
    );
    this.addCleanup(
      this.context.bus.on('navigate:home', () => {
        this.navigate({ kind: 'home' });
      }),
    );
    this.addCleanup(
      this.context.bus.on('navigate:graph', () => {
        this.navigate({ kind: 'graph' });
      }),
    );
    this.addCleanup(
      this.context.bus.on('navigate:concept-map', () => {
        this.navigate({ kind: 'concept-map' });
      }),
    );
    this.addCleanup(
      this.context.bus.on<{ id: string; conceptId?: string }>('navigate:tappa', ({ id, conceptId }) => {
        this.navigate({ kind: 'tappa', id, conceptId });
      }),
    );
    this.addCleanup(
      this.context.bus.on<{ id: string; sourceAuthorId?: string }>('navigate:concept', ({ id, sourceAuthorId }) => {
        this.navigate({ kind: 'concept', id, sourceAuthorId });
      }),
    );
    this.addCleanup(
      this.context.bus.on<{ id: string }>('navigate:argomento', ({ id }) => {
        this.navigate({ kind: 'argomento', id });
      }),
    );
    // "← Indietro": torna alla rotta E allo stato da cui si è partiti.
    this.addCleanup(
      this.context.bus.on('navigate:back', () => {
        const prev = this.history.pop();
        this.navigate(prev?.route ?? { kind: 'home' }, {
          isBack: true,
          restoreState: prev?.state,
          restoreScroll: prev?.scroll,
        });
      }),
    );
    // Bridge "navigate" command from host is re-emitted on the bus as "navigate"
    // by AppShell.handleCommand. Normalize it into our taxonomy.
    this.addCleanup(
      this.context.bus.on<{ target?: string; id?: string }>('navigate', (payload) => {
        if (payload.target === 'author' && payload.id) {
          this.navigate({ kind: 'author', id: payload.id });
        } else if (payload.target === 'home' || !payload.target) {
          this.navigate({ kind: 'home' });
        }
      }),
    );

    // Forward snapshot requests to the current view. The view is responsible
    // for producing the image; if it doesn't subscribe, fall back to a stub.
    this.addCleanup(
      this.context.bus.on<{ callback: (dataUrl: string) => void }>(
        'capture:snapshot',
        ({ callback }) => {
          callback(
            'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
          );
        },
      ),
    );

    await this.mountRoute(this.currentRoute);
  }

  private async navigate(
    route: Route,
    opts: { isBack?: boolean; restoreState?: unknown; restoreScroll?: number } = {},
  ): Promise<void> {
    if (sameRoute(route, this.currentRoute)) return;
    const scroll = this.viewContainer?.scrollTop ?? 0;
    if (this.currentRoute.kind === 'home') this.savedHomeScroll = scroll;
    // In avanti: impila rotta+stato+scroll correnti così il "← Indietro" ci
    // riporta ESATTAMENTE qui (vista, selezione/modalità E posizione di scroll —
    // niente "salto a inizio pagina"). All'indietro NON si impila.
    if (!opts.isBack) {
      const capture = (this.currentView as { captureState?: () => unknown } | null)?.captureState?.();
      this.history.push({ route: this.currentRoute, state: capture, scroll });
    }
    this.currentRoute = route;
    await this.mountRoute(route, opts.restoreState, opts.restoreScroll);
  }

  private async mountRoute(route: Route, restoreState?: unknown, restoreScroll?: number): Promise<void> {
    // Tear down the previous view
    if (this.currentView) {
      this.currentView.hide();
      this.currentView.dispose();
      this.currentView = null;
      this.viewContainer.innerHTML = '';
    }

    const slot = document.createElement('div');
    slot.className = 'stage-view-slot';
    this.viewContainer.appendChild(slot);

    switch (route.kind) {
      case 'home':
        this.currentView = new AuthorListView(slot, this.context);
        break;
      case 'author':
        this.currentView = new AuthorDetailView(slot, this.context, route.id);
        break;
      case 'graph':
        this.currentView = new InfluenceGraphView(
          slot,
          this.context,
          (restoreState as InfluenceState | undefined) ?? undefined,
        );
        break;
      case 'concept-map':
        this.currentView = new ConceptMapView(
          slot,
          this.context,
          (restoreState as ConceptMapState | undefined) ?? undefined,
        );
        break;
      case 'tappa':
        this.currentView = new TappaReaderView(slot, this.context, route.id);
        break;
      case 'concept':
        this.currentView = new ConceptPathView(slot, this.context, route.id, route.sourceAuthorId);
        break;
      case 'argomento':
        this.currentView = new ArgumentDetailView(slot, this.context, route.id);
        break;
    }

    await this.currentView.init();
    this.currentView.show();

    // Ripristina la posizione di scroll: all'indietro quella salvata nello stack
    // (torna dove eri), altrimenti — rientrando in home in avanti — l'ultima nota.
    const targetScroll = restoreScroll ?? (route.kind === 'home' ? this.savedHomeScroll : 0);
    if (targetScroll > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.viewContainer.scrollTop = targetScroll;
        });
      });
    }

    // Emit so the sidebar ViewNav can sync its active toggle state.
    this.context.bus.emit<{ route: string }>('stage:route-changed', {
      route: route.kind,
    });
  }

  protected onDispose(): void {
    if (this.currentView) {
      this.currentView.dispose();
      this.currentView = null;
    }
  }
}

/** Due rotte "uguali" (evita di impilare duplicati e ri-montare inutilmente). */
function sameRoute(a: Route, b: Route): boolean {
  if (a.kind !== b.kind) return false;
  if ('id' in a && 'id' in b) return a.id === b.id;
  return true;
}
